import CustomerLocationMarker from '@/components/order/shippingOptions/ClickAndCollect/CustomerLocationMarker';
import PharmacyLocationMarker from '@/components/order/shippingOptions/ClickAndCollect/PharmacyLocationMarker';
import settings from '@/constants/constants';
import { useClickAndCollectCamera } from '@/hooks/order/useClickAndCollectCamera';
import { useClickAndCollectMapSize } from '@/hooks/order/useClickAndCollectMapSize';
import { useUserAddressGeometryQuery } from '@/hooks/order/useUserAddressGeometryQuery';
import { Logger } from '@/utils/logger';
import type { HttpStatus } from '@montugroup/apis-styleguide';
import type { apiCustomerPortalContract } from '@montugroup/circuit-api-contracts';
import type { Address } from '@montugroup/circuit-shared/types/address';
import { MapView, Marker } from '@montugroup/design-system/MapView';
import type { ClientInferResponseBody } from '@ts-rest/core';
import { useState } from 'react';

const MAP_ID = 'cnc_map';

const logger = new Logger('ClickAndCollectMap');

type ClickAndCollectOption = ClientInferResponseBody<
  typeof apiCustomerPortalContract.listCartDeliveryOptionsAndReserveInventory,
  typeof HttpStatus.OK
>['data']['ClickAndCollect']['0'];

type Props = {
  locationQuery: Address;
  selectedPharmacy: ClickAndCollectOption | null;
  confirmedPharmacy: ClickAndCollectOption | null;
  handleMarkerClicked: (id: number) => void;
  clickAndCollectOptions: ClickAndCollectOption[];
};

export function ClickAndCollectMap({
  locationQuery,
  clickAndCollectOptions,
  selectedPharmacy,
  confirmedPharmacy,
  handleMarkerClicked,
}: Props) {
  const mapSize = useClickAndCollectMapSize();
  const [hoveredLocation, setHoveredLocation] = useState<(typeof clickAndCollectOptions)[0] | null>(null);
  const { data: userAddressGeometry, isLoading: isUserAddressGeometryLoading } = useUserAddressGeometryQuery();
  const searchedLocation = locationQuery?.geometry?.location || userAddressGeometry || null;
  const { initialCamera, handleHomeClick } = useClickAndCollectCamera({
    mapId: MAP_ID,
    clickAndCollectOptions,
    searchedLocation,
    selectedPharmacy,
    confirmedPharmacy,
  });

  return (
    <MapView
      zoomControl
      id={MAP_ID}
      isLoading={!initialCamera || isUserAddressGeometryLoading}
      height={mapSize}
      initialCamera={initialCamera}
      onHomeClick={handleHomeClick}
      googleMapId={settings.googleMapIds.clickAndCollect}
      onInitFailure={() => logger.error('Failed to initialize map')}
    >
      {searchedLocation && (
        <Marker position={searchedLocation}>
          <CustomerLocationMarker />
        </Marker>
      )}
      {clickAndCollectOptions.map((location) => (
        <Marker
          onClick={() => handleMarkerClicked(location.pharmacy.id)}
          onMouseEnter={() => setHoveredLocation(location)}
          onMouseLeave={() => setHoveredLocation(null)}
          key={location.pharmacy.id}
          position={{
            lat: location.pharmacyLocation.coordinates.latitude,
            lng: location.pharmacyLocation.coordinates.longitude,
          }}
        >
          <PharmacyLocationMarker
            title={location.pharmacy.name}
            expanded={
              hoveredLocation?.pharmacy.id === location.pharmacy.id ||
              selectedPharmacy?.pharmacy.id === location.pharmacy.id
            }
          />
        </Marker>
      ))}
    </MapView>
  );
}

export default ClickAndCollectMap;
